import { CheckCircle2, Circle, GitPullRequestArrow, ShieldCheck, XCircle } from 'lucide-react';
import {
  formatSedDateTime,
  sedRouteStatusLabel,
  sedRouteStepLabel,
  type SedRouteDetails,
} from '@/lib/sed';
import { cn } from '@/lib/utils';

export function SedRouteBlock({
  details,
  loading,
  error,
  onOpenForm,
}: {
  details: SedRouteDetails | null;
  loading: boolean;
  error: string;
  onOpenForm?: () => void;
}) {
  const instance = details?.instance || null;
  const templates = details?.templates || [];

  return (
    <section className="rounded-2xl border bg-card p-3">
      <div className="flex items-center gap-2">
        <GitPullRequestArrow className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">Маршрут</h3>
        {instance && (
          <span
            className={cn(
              'ml-auto rounded-full border px-2 py-0.5 text-[10px] font-medium',
              instance.status === 'running'
                ? 'border-sky-500/30 bg-sky-500/10 text-sky-700'
                : instance.status === 'approved'
                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700'
                  : instance.status === 'rejected'
                    ? 'border-red-500/30 bg-red-500/10 text-red-700'
                    : 'border-border bg-muted text-muted-foreground',
            )}
          >
            {sedRouteStatusLabel(instance.status)}
            {instance.status === 'running' ? ` · ${instance.currentStep}/${instance.stepsTotal}` : ''}
          </span>
        )}
      </div>

      {loading && <p className="mt-2 text-xs text-muted-foreground">Загрузка маршрута…</p>}

      {!loading && error && (
        <p className="mt-2 rounded-lg border border-red-500/30 bg-red-500/10 px-2 py-1.5 text-[11px] text-red-700">
          {error}
        </p>
      )}

      {!loading && !error && instance && (
        <div className="mt-2 space-y-1">
          {instance.steps.map((step) => {
            const current = instance.status === 'running' && step.order === instance.currentStep;
            return (
              <div
                key={`${instance.id}-${step.order}`}
                className={cn(
                  'flex items-start gap-2 rounded-xl px-2 py-1.5',
                  current && 'border border-primary/40 bg-primary/5',
                )}
              >
                <span className="mt-0.5 shrink-0">
                  {step.status === 'approve' ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  ) : step.status === 'reject' ? (
                    <XCircle className="h-3.5 w-3.5 text-red-600" />
                  ) : (
                    <Circle className={cn('h-3.5 w-3.5', current ? 'text-primary' : 'text-muted-foreground')} />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-xs font-medium">
                      {step.order}. {step.name}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {step.kind === 'sign' ? 'подпись' : 'согласование'}
                      {step.assigneeType === 'role' && step.assigneeValue ? ` · роль: ${step.assigneeValue}` : ''}
                    </span>
                    <span className="text-[10px] text-muted-foreground">{sedRouteStepLabel(step.status)}</span>
                  </div>
                  {step.comment && (
                    <div className="mt-0.5 whitespace-pre-wrap break-words text-[11px] text-muted-foreground">
                      «{step.comment}»
                    </div>
                  )}
                  {step.actedAt && (
                    <div className="text-[10px] text-muted-foreground">{formatSedDateTime(step.actedAt)}</div>
                  )}
                </div>
              </div>
            );
          })}
          {instance.startedAt && (
            <div className="pt-1 text-[10px] text-muted-foreground">
              Запущен: {formatSedDateTime(instance.startedAt)}
            </div>
          )}
        </div>
      )}

      {!loading && !error && !instance && templates.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {templates.map((template) => (
            <div key={template.code} className="rounded-xl border bg-muted/20 px-2 py-1.5">
              <div className="flex items-center gap-1.5 text-xs font-medium">
                <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                {template.synonym || template.code}
              </div>
              <div className="mt-0.5 text-[11px] leading-5 text-muted-foreground">
                {template.steps.map((step) => step.name).join(' → ')}
              </div>
            </div>
          ))}
          <p className="text-[10px] text-muted-foreground">
            Маршрут ещё не запущен. Запуск и решения — в форме документа.
          </p>
        </div>
      )}

      {!loading && !error && !instance && templates.length === 0 && (
        <p className="mt-2 text-[11px] text-muted-foreground">Маршрут для этого документа не настроен.</p>
      )}

      {onOpenForm && !loading && !error && (
        <button
          type="button"
          onClick={onOpenForm}
          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary/30 bg-primary/5 px-2 py-1.5 text-[11px] font-medium text-primary hover:bg-primary/10"
        >
          Согласование и подписи — в форме документа
        </button>
      )}
    </section>
  );
}
